import type {
  BackfillFilter,
  BackfillRange,
  FirehoseEvent,
  FirehoseTransport,
  PublishAck,
  SeqEvent,
  SubscribeFilter,
  SubscribeHooks,
} from "./types.js";
import { BackfillResetError, RelayError } from "./relay-error.js";
import { lineFrames, sseEvents } from "./sse.js";

export interface RelayTransportOptions {
  baseUrl: string;
  /** Injectable for tests; defaults to global fetch. */
  fetchImpl?: typeof fetch;
}

function b64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

function rowToSeqEvent(j: {
  seq: number;
  id: string;
  type: string;
  cells?: string[];
  receivedAt?: number;
  bytes: string;
}): SeqEvent {
  return {
    seq: j.seq,
    id: j.id,
    type: j.type,
    cells: j.cells ?? [],
    bytes: b64ToBytes(j.bytes),
    ...(j.receivedAt !== undefined ? { receivedAt: j.receivedAt } : {}),
  };
}

/** FirehoseTransport over the A1 relay HTTP contract (services/relay/README.md).
 *  Reconnect-from-cursor is owned by F6's Subscription, which wraps this. */
export class RelayTransport implements FirehoseTransport {
  #abort = new AbortController();
  /** Most recent `simulation` flag observed on /info or an SSE hello frame. */
  lastSimulation = false;
  /** Most recent `firstSeq` (purge floor) observed on a hello frame (FH-5).
   *  Leaky/informational — kept off the neutral consumer contract (ADR-0014);
   *  the actionable signal is the `onReset` hook, not this integer. */
  lastFirstSeq = 0;

  constructor(private readonly opts: RelayTransportOptions) {}

  private get fetch(): typeof fetch {
    return this.opts.fetchImpl ?? globalThis.fetch.bind(globalThis);
  }

  /** One-shot request that still aborts on close(), WITHOUT leaking an abort listener on the
   *  long-lived #abort signal per call. undici keeps a per-request listener on the signal it is given
   *  for the request's lifetime; reusing one signal across thousands of publishes piled them up
   *  (MaxListenersExceededWarning → eventual memory growth). Each call instead gets a fresh signal,
   *  linked to #abort only for the duration of the call (the linkage listener is removed in finally).
   *  Use for non-streaming calls; streaming readers keep #abort.signal for the stream's lifetime. */
  async #req(url: string, init: RequestInit = {}): Promise<Response> {
    const parent = this.#abort.signal;
    if (parent.aborted) throw new DOMException("RelayTransport closed", "AbortError");
    const ctrl = new AbortController();
    const onAbort = () => ctrl.abort((parent as AbortSignal).reason);
    parent.addEventListener("abort", onAbort, { once: true });
    try {
      return await this.fetch(url, { ...init, signal: ctrl.signal });
    } finally {
      parent.removeEventListener("abort", onAbort);
    }
  }

  async publish(evt: FirehoseEvent): Promise<PublishAck> {
    const res = await this.#req(`${this.opts.baseUrl}/publish`, {
      method: "POST",
      headers: { "content-type": "application/octet-stream" },
      // Uint8Array is a valid BufferSource body; cast for the TS 5.x generic-typed-array friction.
      // Use the global fetch's body param type so this resolves under both DOM and @types/node libs.
      body: evt.bytes as NonNullable<Parameters<typeof fetch>[1]>["body"],
    });
    if (!res.ok) throw await RelayError.from(res);
    const j = (await res.json()) as { seq: number; duplicate?: boolean };
    return { seq: j.seq, duplicate: !!j.duplicate };
  }

  async *subscribe(filter: SubscribeFilter, hooks?: SubscribeHooks): AsyncIterable<SeqEvent> {
    const qs = new URLSearchParams();
    if (filter.cells) qs.set("cells", filter.cells.join(","));
    if (filter.types) qs.set("types", filter.types.join(","));
    if (filter.sinceSeq !== undefined) qs.set("sinceSeq", String(filter.sinceSeq));
    else if (filter.lookback !== undefined) qs.set("lookback", String(filter.lookback));
    if (filter.class) qs.set("class", filter.class);
    const res = await this.fetch(`${this.opts.baseUrl}/subscribe?${qs}`, {
      headers: { accept: "text/event-stream" },
      signal: this.#abort.signal,
    });
    if (!res.ok) throw await RelayError.from(res);
    if (!res.body) return;
    for await (const frame of sseEvents(res.body)) {
      if (frame.event === "hello") {
        try {
          const h = JSON.parse(frame.data) as { simulation?: boolean; firstSeq?: number; reset?: boolean };
          this.lastSimulation = !!h.simulation;
          this.lastFirstSeq = h.firstSeq ?? 0;
          if (h.reset) hooks?.onReset?.();
        } catch {
          /* tolerate a malformed hello */
        }
        continue;
      }
      if (frame.event === "caughtUp") {
        try {
          const c = JSON.parse(frame.data) as { seq?: number };
          hooks?.onCaughtUp?.(typeof c.seq === "number" ? c.seq : 0);
        } catch {
          /* tolerate a malformed caughtUp */
        }
        continue;
      }
      if (frame.event !== "evt") continue;
      yield rowToSeqEvent(JSON.parse(frame.data));
    }
  }

  async *backfill(filter: BackfillFilter, range: BackfillRange): AsyncIterable<SeqEvent> {
    const qs = new URLSearchParams({ fromSeq: String(range.fromSeq), toSeq: String(range.toSeq) });
    if (filter.cells) qs.set("cells", filter.cells.join(","));
    if (filter.types) qs.set("types", filter.types.join(","));
    if (filter.class) qs.set("class", filter.class);
    const res = await this.fetch(`${this.opts.baseUrl}/backfill?${qs}`, { signal: this.#abort.signal });
    if (res.status === 409) {
      const body = (await res.json().catch(() => ({}))) as { firstSeq?: number };
      throw new BackfillResetError(body.firstSeq ?? 0);
    }
    if (!res.ok) throw await RelayError.from(res);
    if (!res.body) return;
    for await (const line of lineFrames(res.body)) {
      if (line.trim() === "") continue;
      yield rowToSeqEvent(JSON.parse(line));
    }
  }

  /** Durable keyed read (FH-3 /getLatest): the latest event for a compacted
   *  subject suffix, or null when the key has no message. Durable-only by the
   *  gateway, so this takes just the key (class is fixed to "durable"). */
  async getLatest(key: string): Promise<SeqEvent | null> {
    const qs = new URLSearchParams({ class: "durable", key });
    const res = await this.#req(`${this.opts.baseUrl}/getLatest?${qs}`);
    if (res.status === 404) return null;
    if (!res.ok) throw await RelayError.from(res);
    return rowToSeqEvent((await res.json()) as Parameters<typeof rowToSeqEvent>[0]);
  }

  /** Durable identity chain (FH-3 /getChain): every link for a DID in ascending
   *  seq (genesis declareKeys + rotateKey links). Empty when the DID is unknown. */
  async *getChain(did: string): AsyncIterable<SeqEvent> {
    const qs = new URLSearchParams({ did });
    const res = await this.fetch(`${this.opts.baseUrl}/getChain?${qs}`, { signal: this.#abort.signal });
    if (!res.ok) throw await RelayError.from(res);
    if (!res.body) return;
    for await (const line of lineFrames(res.body)) {
      if (line.trim() === "") continue;
      yield rowToSeqEvent(JSON.parse(line));
    }
  }

  async info(): Promise<{ simulation: boolean; headSeq: number }> {
    const res = await this.#req(`${this.opts.baseUrl}/info`);
    if (!res.ok) throw await RelayError.from(res);
    const j = (await res.json()) as { simulation?: boolean; headSeq?: number };
    this.lastSimulation = !!j.simulation;
    return { simulation: this.lastSimulation, headSeq: j.headSeq ?? 0 };
  }

  async head(): Promise<number> {
    return (await this.info()).headSeq;
  }

  /** True once close() has aborted in-flight requests. */
  get aborted(): boolean {
    return this.#abort.signal.aborted;
  }

  async close(): Promise<void> {
    this.#abort.abort();
  }
}
